import React from 'react';
import { ShieldCheck, Lock, Zap, Heart, Target, Users } from 'lucide-react';
import { PageHero, Card } from '../components/ui';

const ValueCard = ({ icon: Icon, title, description }) => (
    <div className="flex items-start space-x-4 p-5 rounded-xl bg-slate-50 dark:bg-slate-700/50 transition-all hover:bg-brand-50 dark:hover:bg-brand-900/20 group">
        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 group-hover:scale-110 transition-transform">
            <Icon size={24} />
        </div>
        <div>
            <h3 className="font-semibold text-slate-800 dark:text-white mb-1">{title}</h3>
            <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">{description}</p>
        </div>
    </div>
);

export default function AboutPage() {
    return (
        <div className="bg-slate-50 dark:bg-slate-900 min-h-screen transition-colors duration-200">
            <PageHero
                title="About Gift Scout"
                subtitle="Your trusted partner in smart savings and gift card management."
            >
                <img
                    src="/images/logo_with_gift_box.webp"
                    alt="Gift Scout Mascot"
                    className="h-28 mx-auto mb-6"
                    width="112"
                    height="112"
                    loading="eager"
                />
            </PageHero>

            <main className="container mx-auto px-4 -mt-16 relative z-10 pb-16 max-w-4xl space-y-8">
                {/* Our Story */}
                <Card>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6 flex items-center">
                        <Heart className="mr-3 text-brand-600 dark:text-brand-400" size={28} />
                        Our Story
                    </h2>
                    <div className="text-base text-slate-700 dark:text-slate-300 leading-relaxed space-y-4">
                        <p>
                            Gift Scout was born from a simple frustration: <strong className="text-brand-700 dark:text-brand-400">tracking gift cards shouldn't be this hard.</strong>
                        </p>
                        <p>
                            Like many savvy shoppers, we discovered the world of discounted gift cards—buying cards at 10%, 15%, even 20% off face value. The savings were incredible, but managing dozens of cards across multiple retailers quickly became a nightmare. Spreadsheets got messy, balances were forgotten, and cards expired unused.
                        </p>
                        <p>
                            So we built something better. What started as a simple personal project has evolved into a powerful, secure platform that helps you maximize every dollar of your gift card savings.
                        </p>
                    </div>
                </Card>

                {/* Mission */}
                <Card>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6 flex items-center">
                        <Target className="mr-3 text-brand-600 dark:text-brand-400" size={28} />
                        Our Mission
                    </h2>
                    <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">
                        We're on a mission to empower smart shoppers everywhere. Whether you're managing existing gift cards, hunting for the best deals, or streamlining your checkout experience with our browser extension—Gift Scout is here to help you <strong className="text-brand-700 dark:text-brand-400">save more with less effort</strong>.
                    </p>
                </Card>

                {/* Values */}
                <Card>
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6 flex items-center">
                        <Users className="mr-3 text-brand-600 dark:text-brand-400" size={28} />
                        What We Stand For
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <ValueCard
                            icon={ShieldCheck}
                            title="Privacy First"
                            description="Your data belongs to you. We use end-to-end encryption so only you can access your sensitive gift card information."
                        />
                        <ValueCard
                            icon={Lock}
                            title="Security by Design"
                            description="Your encryption key never leaves your device. Even we can't see your card numbers—and that's exactly how it should be."
                        />
                        <ValueCard
                            icon={Zap}
                            title="Effortless Savings"
                            description="From auto-fill at checkout to balance tracking, we make managing gift cards as simple as possible."
                        />
                        <ValueCard
                            icon={Heart}
                            title="Built with Care"
                            description="Gift Scout is crafted by users, for users. We use it ourselves every day and constantly improve based on real needs."
                        />
                    </div>
                </Card>

                {/* Security Highlight */}
                <div className="bg-gradient-to-r from-brand-700 to-brand-800 dark:from-brand-800 dark:to-brand-900 rounded-xl shadow-card-prominent p-8 text-white">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        <div className="mb-6 md:mb-0 md:mr-8">
                            <h2 className="text-2xl font-bold mb-3">Your Security Matters</h2>
                            <p className="text-brand-100 leading-relaxed">
                                Gift Scout features bank-grade encryption. Your gift card codes are encrypted with a PIN that only you know.
                                We physically cannot access your data—and that's a feature, not a limitation.
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                <Lock size={40} className="text-white" />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
